import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import ApiService from "../../../Services/apiService";

// This component mirrors Angular's "template-document-form" concept:
// - Renders template HTML with dynamic inputs/selects
// - Collects values into a JSON payload (a1..a33 + tableData)
// - Emits payload upward when parent wants to submit

const buildEmptyModel = () => {
	const model = { tableData: [] };
	for (let i = 1; i <= 33; i++) model[`a${i}`] = "";
	return model;
};

const escapeHtmlAttr = s =>
	String(s ?? "")
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;");

const TemplateDocumentForm = forwardRef(function TemplateDocumentForm(
	{ templateId, documentBodyData, onSubmitTemplate },
	ref,
) {
	const containerRef = useRef(null);
	const lastStudentsListPosRef = useRef(null); // a{pos} where students-list stored
	const lastStudentsDivisionIdRef = useRef(null);
	const prefetchedExtraForTemplateIdRef = useRef(null);
	const templateBodyLoadedForTemplateIdRef = useRef(null);

	// Keep raw template HTML separate from rendered HTML to avoid render loops
	const [templateBodyRaw, setTemplateBodyRaw] = useState("");
	const [renderedHtml, setRenderedHtml] = useState("");
	const [templateInfo, setTemplateInfo] = useState([]);
	const [templateModel, setTemplateModel] = useState(buildEmptyModel());
	const [tableData, setTableData] = useState([]);
	const [tableRowCount, setTableRowCount] = useState(1);
	const [tableTds, setTableTds] = useState([]);
	const [tableLibDatas, setTableLibDatas] = useState({});
	const [tableListDatas, setTableListDatas] = useState({});
	const [libraryDatas, setLibraryDatas] = useState({});
	const [listDatas, setListDatas] = useState({});

	const [groupData, setGroupData] = useState([]);
	const loadedGroupsRef = useRef(false);
	const loadedExtraDataRef = useRef(false);
	const [extrasReady, setExtrasReady] = useState(false);
	const [educationUnits, setEducationUnits] = useState([]);

	const initialModel = useMemo(() => {
		if (!documentBodyData) return null;
		try {
			return JSON.parse(documentBodyData);
		} catch {
			return null;
		}
	}, [documentBodyData]);

	useEffect(() => {
		if (!templateId) return;
		loadedGroupsRef.current = false;
		loadedExtraDataRef.current = false;
		prefetchedExtraForTemplateIdRef.current = null;
		setExtrasReady(false);

		// Clear previous template UI immediately (so user doesn't see old template while loading new)
		setRenderedHtml("");
		setTemplateBodyRaw("");
		setTemplateInfo([]);
		setLibraryDatas({});
		setListDatas({});
		setTableLibDatas({});
		setTableListDatas({});
		load(templateId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateId]);

	const setEducationalUnits = useCallback((data) => {
		const result = [];
		const seen = new Set();

		const visit = node => {
			if (!node) return;
			if (Array.isArray(node)) {
				node.forEach(visit);
				return;
			}

			const shouldTakeDivisions =
				node?.divisionAttachmentOn === true &&
				node?.lecturerAttachmentOn === false &&
				Array.isArray(node?.divisions) &&
				node.divisions.length > 0;

			if (shouldTakeDivisions) {
				node.divisions.forEach(d => {
					const key = d?.id ?? JSON.stringify(d);
					if (key == null) return;
					if (seen.has(key)) return;
					seen.add(key);
					result.push(d);
				});
			}

			if (Array.isArray(node?.children) && node.children.length) {
				node.children.forEach(visit);
			}
		};

		visit(data);

		return result;
	}, []);


	// Handle dropdown (entityId === 6) change: reset dependent fields once, then load groups
	const lastEduValueRef = useRef(null);
	const eduPosRef = useRef(null);
	const eduChangedByUserRef = useRef(false);
	const eduInitializedRef = useRef(false);

	useEffect(() => {
		const eduInfo = (templateInfo || []).find(
			x => x.type === "library" && Number(x.entityId) === 6,
		);
		eduPosRef.current = eduInfo?.position ? Number(eduInfo.position) : null;
	}, [templateInfo]);

	// Old groups source: only for specific templates (documentTemplateId === 11)
	useEffect(() => {
		if (!templateId) return;
		if (!templateInfo?.length) return;
		if (loadedGroupsRef.current) return;

		const isTemplate11 = (templateInfo || []).some(
			x => Number(x.documentTemplateId) === 11,
		);
		if (!isTemplate11) return;

		// If educational units dropdown exists, groups must come from selected unit instead
		const usesEducationalUnits = (templateInfo || []).some(
			x => x.type === "library" && Number(x.entityId) === 6,
		);
		if (usesEducationalUnits) return;

		loadedGroupsRef.current = true;
		ApiService.getAllStudentDivisions()
			.then(groupsRes => {
				if (groupsRes?.data) setGroupData(groupsRes.data);
			})
			.catch(() => { });
	}, [templateId, templateInfo]);

	useEffect(() => {
		if (!templateId) return;
		if (!templateInfo?.length) return;

		const eduInfo = (templateInfo || []).find(
			x => x.type === "library" && Number(x.entityId) === 6,
		);
		if (!eduInfo?.position) return;

		const eduPos = Number(eduInfo.position);
		const eduValue = templateModel?.[`a${eduPos}`] ?? "";

		// Prevent loops, but still allow re-run when educational units finish loading
		// (edit/update mode can have saved eduValue before educationUnits arrives)
		const eduRunKey = `${eduValue}::${Array.isArray(educationUnits) ? educationUnits.length : 0}`;
		if (lastEduValueRef.current === eduRunKey) return;
		lastEduValueRef.current = eduRunKey;

		// find dependent positions safely
		const groupInfo = (templateInfo || []).find(
			x =>
				Number(x.position) === eduPos + 1 &&
				(x.type === "drop-down-group" || x.type === "drop-down-group-not-draw"),
		);
		const groupPos = groupInfo?.position ? Number(groupInfo.position) : eduPos + 1;
		const studentsInfo = (templateInfo || []).find(
			x => Number(x.position) === groupPos + 1 && x.type === "students-list",
		);
		const studentsPos = studentsInfo?.position ? Number(studentsInfo.position) : groupPos + 1;

		// On initial load (edit/update), DON'T reset saved group/students;
		// just build groupData based on saved educational unit.
		if (!eduInitializedRef.current) {
			eduInitializedRef.current = true;
			if (!eduValue) return;
			const currentUnit =
				(educationUnits || []).find(u => String(u?.name ?? "") === String(eduValue)) ||
				(educationUnits || []).find(u => String(u?.id ?? "") === String(eduValue));
			if (!currentUnit) return;
			setGroupData(setEducationalUnits(currentUnit));
			return;
		}

		// Only reset dependents when user actually changed educational unit
		if (!eduChangedByUserRef.current) {
			// programmatic change/hydration: just refresh groupData if possible
			if (!eduValue) return;
			const currentUnit =
				(educationUnits || []).find(u => String(u?.name ?? "") === String(eduValue)) ||
				(educationUnits || []).find(u => String(u?.id ?? "") === String(eduValue));
			if (!currentUnit) return;
			setGroupData(setEducationalUnits(currentUnit));
			return;
		}
		eduChangedByUserRef.current = false;

		// Reset dependent state/UI immediately
		setGroupData([]);

		const root = containerRef.current;
		const groupSelect = root?.querySelector(
			`#${CSS?.escape ? CSS.escape(`a${groupPos}`) : `a${groupPos}`}`,
		);
		if (groupSelect?.tagName === "SELECT") groupSelect.value = "";

		const existingStudents = root?.querySelector(".students-container");
		if (existingStudents) existingStudents.remove();
		lastStudentsListPosRef.current = null;
		lastStudentsDivisionIdRef.current = null;

		setTemplateModel(prev => {
			const next = {
				...prev,
				[`a${groupPos}`]: "",
				[`a${studentsPos}`]: [],
			};
			// avoid extra renders if already cleared
			if (prev?.[`a${groupPos}`] === next[`a${groupPos}`] && Array.isArray(prev?.[`a${studentsPos}`]) && prev[`a${studentsPos}`].length === 0) {
				return prev;
			}
			return next;
		});

		if (!eduValue) return;

		// Build groups from selected educational unit object
		const currentUnit =
			(educationUnits || []).find(u => String(u?.name ?? "") === String(eduValue)) ||
			(educationUnits || []).find(u => String(u?.id ?? "") === String(eduValue));
		if (!currentUnit) return;

		const groups = setEducationalUnits(currentUnit);
		setGroupData(groups);
	}, [templateId, templateInfo, templateModel, educationUnits, setEducationalUnits]);



	useEffect(() => {
		if (!templateId) return;
		if (!templateInfo?.length) return;
		// Ensure body is loaded first: GET /documentTemplate/{id} -> then extras
		if (templateBodyLoadedForTemplateIdRef.current !== templateId) return;
		// Prefetch must be bound to current templateId, otherwise it can run with stale templateInfo.
		if (prefetchedExtraForTemplateIdRef.current === templateId) return;
		prefetchedExtraForTemplateIdRef.current = templateId;
		loadedExtraDataRef.current = true;
		setExtrasReady(false);

		// Prefetch datasets needed by templateInfo types (library/list/table-library/table-list)
		const requests = [];
		templateInfo.forEach(info => {
			if (info.type === "library" && info.entityId) {

				if (info.entityId === 6) {
					requests.push(
						ApiService.getEducationalUnits(1, 100)
							.then(res => {
								if (res?.data) {
									setEducationUnits(res.data);
									setLibraryDatas(prev => ({ ...prev, [info.position]: res.data }));
								}
							})
							.catch(() => { }),
					);
				} else {
					requests.push(
						ApiService.getLibrarydataByLibId(info.entityId)
							.then(res => {
								if (res?.data) {
									setLibraryDatas(prev => ({ ...prev, [info.position]: res.data }));
								}
							})
							.catch(() => { }),
					);
				}
			}
			if (info.type === "list" && info.id) {
				requests.push(
					ApiService.getDocumentTemplateInfoData(info.id)
						.then(res => {
							if (res?.data) {
								setListDatas(prev => ({ ...prev, [info.position]: res.data }));
							}
						})
						.catch(() => { }),
				);
			}
			if (info.type === "table-library" && info.entityId) {
				requests.push(
					ApiService.getLibrarydataByLibId(info.entityId)
						.then(res => {
							if (res?.data) {
								setTableLibDatas(prev => ({ ...prev, [info.position]: res.data }));
							}
						})
						.catch(() => { }),
				);
			}
			if (info.type === "table-list" && info.id) {
				requests.push(
					ApiService.getDocumentTemplateInfoData(info.id)
						.then(res => {
							if (res?.data) {
								setTableListDatas(prev => ({ ...prev, [info.position]: res.data }));
							}
						})
						.catch(() => { }),
				);
			}
		});

		Promise.allSettled(requests).finally(() => setExtrasReady(true));
	}, [templateId, templateInfo]);

	useEffect(() => {
		// Only render template after extra datasets are prefetched for this templateId
		if (!extrasReady) return;
		if (!templateInfo?.length || !templateBodyRaw) return;
		setRenderedHtml(renderHtml());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [extrasReady, templateInfo, templateBodyRaw, groupData, libraryDatas, listDatas, tableLibDatas, tableListDatas, documentBodyData]);

	useEffect(() => {
		// After HTML is mounted, hydrate dynamic table rows and table values from stored model
		if (!renderedHtml) return;
		// NOTE: templateModel is already hydrated from initialModel in load(),
		// so prefer templateModel to keep dropdown changes visible during update/edit.
		const model = { ...buildEmptyModel(), ...templateModel };
		hydrateDomFromModel(model);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [renderedHtml]);

	useEffect(() => {
		// When editing: if group already selected, auto-load students list/table like Angular.
		if (!renderedHtml) return;
		if (!templateInfo?.length) return;
		if (!groupData?.length) return;

		const model = { ...buildEmptyModel(), ...templateModel };
		const groupInfos = templateInfo.filter(
			x => x.type === "drop-down-group" || x.type === "drop-down-group-not-draw",
		);

		groupInfos.forEach(info => {
			const pos = info.position;
			const selectedName = model?.[`a${pos}`];
			if (!selectedName) return;
			const division = (groupData || []).find(g => (g.groupName || g.name) === selectedName);
			if (!division?.id) return;

			const nextPos = pos + 1;
			const nextInfo = templateInfo.find(x => x.position === nextPos);
			if (nextInfo?.type === "students-list") {
				setStudentsListForDivision(division.id, nextPos);
			}
			if (templateInfo.some(x => x.type === "students-in-table")) {
				fillStudentsInTable(division.id);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [renderedHtml, templateInfo, groupData, documentBodyData]);

	const load = async id => {
		const tplRes = await ApiService.getDocumentTemplateById(id);
		const body = tplRes?.data?.body || "";
		templateBodyLoadedForTemplateIdRef.current = id;
		setTemplateBodyRaw(body);
		const infoRes = await ApiService.getDocumentTemplateInfoInTemplateId(id);
		setTemplateInfo(infoRes?.data || []);

		if (initialModel) {
			setTemplateModel(prev => ({ ...prev, ...initialModel }));
			if (Array.isArray(initialModel.tableData)) setTableData(initialModel.tableData);
		} else {
			setTemplateModel(buildEmptyModel());
			setTableData([]);
		}
	};

	const ensureTableHasRows = (rowCountNeeded) => {
		const root = containerRef.current;
		if (!root) return;
		const table = root.querySelector("table");
		if (!table) return;
		const tbody = table.querySelector("tbody") || table;
		const trs = Array.from(tbody.querySelectorAll("tr"));
		if (!trs.length) return;

		// Keep header row if present as first row with "row-th" class (Angular style)
		const dataRows = trs.filter(tr => !tr.classList.contains("row-th"));
		if (!dataRows.length) return;
		const firstDataRow = dataRows[0];

		// If there are more rows than needed, remove from end
		while (dataRows.length > rowCountNeeded) {
			const last = dataRows[dataRows.length - 1];
			tbody.removeChild(last);
			dataRows.pop();
		}

		// Add rows by cloning the last existing data row
		while (dataRows.length < rowCountNeeded) {
			const newRowIndex = dataRows.length + 1; // 1-based
			const clone = (dataRows[dataRows.length - 1] || firstDataRow).cloneNode(true);
			clone.querySelectorAll("input,select,textarea,div[id^='col']").forEach(el => {
				const id = el.getAttribute?.("id");
				if (id) {
					const nextId = id.replace(/-\d+$/, `-${newRowIndex}`);
					el.setAttribute("id", nextId);
				}
				if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") el.value = "";
				if (el.tagName === "SELECT") el.selectedIndex = 0;
			});
			const firstTd = clone.querySelector("td");
			if (firstTd) firstTd.textContent = String(newRowIndex);
			tbody.appendChild(clone);
			dataRows.push(clone);
		}
	};

	const hydrateDomFromModel = (model) => {
		const root = containerRef.current;
		if (!root || !model) return;

		// hydrate non-table inputs/selects/textareas by ids a1..a33
		for (let i = 1; i <= 33; i++) {
			const key = `a${i}`;
			const value = model[key];
			if (value === undefined || value === null) continue;

			const el = root.querySelector(`#${CSS?.escape ? CSS.escape(key) : key}`);
			if (!el) continue;
			if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
				el.value = Array.isArray(value) ? "" : String(value);
			} else if (el.tagName === "SELECT") {
				el.value = String(value);
			}
		}

		// hydrate table rows/values from tableData records
		const tData = Array.isArray(model.tableData) ? model.tableData : [];
		if (tData.length) {
			const maxRow = tData.reduce((mx, it) => Math.max(mx, Number(it?.number || 0)), 1);
			ensureTableHasRows(maxRow);

			tData.forEach(it => {
				const row = String(it.number);
				const col = String(it.columnNumber);
				if (!row || !col) return;

				if (Array.isArray(it.value)) {
					// table-number-and-date like Angular: value[0] number, value[1] date
					const numEl = root.querySelector(`#${CSS?.escape ? CSS.escape(`0-col${col}-${row}`) : `0-col${col}-${row}`}`);
					const dateEl = root.querySelector(`#${CSS?.escape ? CSS.escape(`1-col${col}-${row}`) : `1-col${col}-${row}`}`);
					if (numEl && it.value[0] !== undefined && it.value[0] !== null) numEl.value = String(it.value[0]);
					if (dateEl && it.value[1] !== undefined && it.value[1] !== null) dateEl.value = String(it.value[1]);
					return;
				}

				if (it.timeType === "start" || it.timeType === "end") {
					const id = `${it.timeType}col${col}-${row}`;
					const el = root.querySelector(`#${CSS?.escape ? CSS.escape(id) : id}`);
					if (el) el.value = it.value ? String(it.value) : "";
					return;
				}

				const id = `col${col}-${row}`;
				const el = root.querySelector(`#${CSS?.escape ? CSS.escape(id) : id}`);
				if (!el) return;
				if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT") {
					el.value = it.value ? String(it.value) : "";
				}
			});
		}
	};

	const renderStudentsCheckboxesHtml = (divisionId, students, selectedNames, defaultChecked = false) => {
		const selectedSet = new Set((selectedNames || []).map(x => String(x)));
		let str = `<div id="students-${divisionId}" class="students-container">`;
		(students || []).forEach((s, idx) => {
			const fullName = s?.studentName
				? String(s.studentName)
				: `${s?.lastname || ""} ${s?.firstname || ""} ${s?.middlename ? s.middlename : ""}`.trim();
			const checkboxId = `${divisionId}-${idx + 1}`;
			const checked = defaultChecked || selectedSet.has(fullName) ? ` checked="checked" ` : "";
			str += `<input ${checked} type="checkbox" id="${checkboxId}"><label for="${checkboxId}">${fullName}</label><br>`;
		});
		str += `</div>`;
		return str;
	};

	const setStudentsListForDivision = async (divisionId, studentsListPosition) => {
		if (!divisionId || !studentsListPosition) return;
		try {
			// Must mirror Angular exactly: /DivisionStudent/GetStudentsByDivision/{id}/false
			const res = await ApiService.getStudentsByDivision(divisionId, false);
			const students = res?.data || [];
			const root = containerRef.current;
			if (!root) return;

			const key = `a${studentsListPosition}`;
			lastStudentsListPosRef.current = studentsListPosition;
			const prevDivisionId = lastStudentsDivisionIdRef.current;
			lastStudentsDivisionIdRef.current = divisionId;

			const model = initialModel ? { ...buildEmptyModel(), ...initialModel } : collectModelFromDom();
			let selected = Array.isArray(model[key]) ? model[key] : null;
			// Treat empty array as "no saved selection" => check all by default
			if (Array.isArray(selected) && selected.length === 0) selected = null;
			const allNames = (students || []).map(s => s?.studentName).filter(Boolean);

			// If group changed (new division), always check all by default.
			if (prevDivisionId && prevDivisionId !== divisionId) {
				selected = null;
			}

			const defaultChecked = !selected;
			const selectedForHtml = selected || allNames;

			const html = renderStudentsCheckboxesHtml(divisionId, students, selectedForHtml, defaultChecked);

			// If students container already rendered, always replace it (supports switching groups)
			const existingContainer = root.querySelector(".students-container");
			if (existingContainer) {
				existingContainer.outerHTML = html;
			} else {
				// try find exact placeholder containing {aX}
				const marker = `{a${studentsListPosition}}`;
				let injected = false;
				root.querySelectorAll("p.hidden").forEach(p => {
					if (injected) return;
					if ((p.innerHTML || "").includes(marker) || (p.textContent || "").includes(marker)) {
						p.outerHTML = html;
						injected = true;
					}
				});
				if (!injected) {
					// fallback: replace first hidden paragraph marker (best effort)
					const placeholder = root.querySelector(`p.hidden`);
					if (placeholder) placeholder.outerHTML = html;
				}
			}

			// If model has no saved list, Angular behavior is "check all by default"
			if (!selected) {
				setTemplateModel(prev => ({ ...prev, [key]: allNames }));
			}
		} catch {
			// ignore
		}
	};

	const fillStudentsInTable = async (divisionId) => {
		if (!divisionId) return;
		try {
			// Must mirror Angular exactly: /DivisionStudent/GetStudentsByDivision/{id}/false
			const res = await ApiService.getStudentsByDivision(divisionId, false);
			const students = res?.data || [];
			if (!students.length) return;

			// Choose first table column position (Angular uses first tableTds)
			const tablePositions = templateInfo
				.filter(info =>
					[
						"table-input-string",
						"table-input-date",
						"table-input-number",
						"table-library",
						"table-list",
						"table-sign-part",
						"table-number-and-date",
						"table-input-start-end-time",
					].includes(info.type),
				)
				.map(x => x.position);
			const firstPos = tablePositions?.[0];
			if (!firstPos) return;

			ensureTableHasRows(students.length);
			const root = containerRef.current;
			if (!root) return;

			// Set student names into the first table column
			const items = [];
			students.forEach((s, idx) => {
				const row = String(idx + 1);
				const id = `col${firstPos}-${row}`;
				const el = root.querySelector(`#${CSS?.escape ? CSS.escape(id) : id}`);
				if (el) el.value = s.studentName || "";
				items.push({ number: row, columnNumber: String(firstPos), value: s.studentName || "", timeType: "" });
			});

			// merge into current model.tableData
			setTableData(items);
		} catch {
			// ignore
		}
	};

	const renderHtml = () => {
		let body = templateBodyRaw;
		let tds = [];
		let rowCount = 1;

		// IMPORTANT: use templateModel so selection changes are reflected immediately in edit/update mode
		const model = { ...buildEmptyModel(), ...templateModel };
		const tableDataFromModel = Array.isArray(model.tableData) ? model.tableData : [];

		const getCommissionNamesForSignBlock = signPosition => {
			const key = `a${signPosition}`;
			const direct = model[key];
			if (Array.isArray(direct) && direct.some(x => x != null && String(x).trim())) {
				return direct.map(x => (x == null ? "" : String(x))).filter(s => s.trim());
			}
			const prevInputs = templateInfo.find(
				x => x.type === "inputs-list" && x.position === signPosition - 1,
			);
			if (prevInputs) {
				const pv = model[`a${prevInputs.position}`];
				if (Array.isArray(pv) && pv.some(x => x != null && String(x).trim())) {
					return pv.map(x => String(x)).filter(s => s.trim());
				}
			}
			const onlyInputs = templateInfo.filter(x => x.type === "inputs-list");
			if (onlyInputs.length === 1) {
				const pv = model[`a${onlyInputs[0].position}`];
				if (Array.isArray(pv) && pv.length) return pv.map(x => String(x)).filter(s => s.trim());
			}
			return [];
		};

		templateInfo.forEach(info => {
			const position = info.position;
			const replacePlaceholder = (html) => {
				// Some templates contain placeholders like {A13} (uppercase A)
				const re = new RegExp(`\\{[aA]${position}\\}`, "g");
				body = body.replace(re, html);
			};

			// table markers
			if (
				info.type === "table-input-string" ||
				info.type === "table-input-date" ||
				info.type === "table-input-number" ||
				info.type === "table-library" ||
				info.type === "table-list" ||
				info.type === "table-sign-part" ||
				info.type === "table-number-and-date" ||
				info.type === "table-input-start-end-time" ||
				info.type === "remove-row-button"
			) {
				tds.push(`<td>{a${position}}</td>`);
			}

			// basic replacements (subset, but covers the common ones)
			if (info.type === "organization-name") {
				const value = templateModel[`a${position}`] || "";
				body = body.replace(
					`<p>{a${position}}</p>`,
					`<input id="a${position}" name="a${position}" autocomplete="off" class="custom-input organization-title center" value="${value}">`,
				);
			} else if (info.type === "input-create-date") {
				body = body.replace(
					`<p>{a${position}}</p>`,
					`<input id="a${position}" name="a${position}" type="date" autocomplete="off" class="custom-input date-input">`,
				);
			} else if (info.type === "input-date") {
				body = body.replace(
					`{a${position}}`,
					`<input id="a${position}" name="a${position}" type="date" autocomplete="off" class="custom-input date-input bottom-short-mrg">`,
				);
			} else if (info.type === "input-number") {
				body = body.replace(
					`<p>№ {a${position}}</p>`,
					`<div class="direction-right"><span class="number-label">№</span><input id="a${position}" name="a${position}" type="number" autocomplete="off" class="custom-input doc-num"></div>`,
				);
				body = body.replace(
					`{a${position}}`,
					`<input id="a${position}" name="a${position}" type="number" autocomplete="off" class="custom-input doc-num">`,
				);
			} else if (info.type === "input-date-to-right") {
				body = body.replace(
					`<p>{a${position}}</p>`,
					`<div class="direction-right"><input id="a${position}" name="a${position}" type="date" autocomplete="off" class="custom-input date-input bottom-short-mrg"></div>`,
				);
			} else if (info.type === "input-string") {
				const placeholder = info.placeholder || "";
				body = body.replace(
					`<h2>{a${position}}</h2>`,
					`<h2 class="center selected-font"><input id="a${position}" name="a${position}" type="text" autocomplete="off" placeholder="${placeholder}" class="custom-input template-input bottom-short-mrg"></h2>`,
				);
				body = body.replace(
					`{a${position}}`,
					`<input id="a${position}" name="a${position}" type="text" autocomplete="off" placeholder="${placeholder}" class="custom-input template-input bottom-short-mrg fit-height">`,
				);
			} else if (info.type === "input-short-string") {
				const placeholder = info.placeholder || "";
				body = body.replace(
					`<p>{a${position}}</p>`,
					`<input id="a${position}" name="a${position}" type="text" autocomplete="off" placeholder="${placeholder}" class="custom-input date-input bottom-short-mrg">`,
				);
			} else if (info.type === "input-long-string") {
				const placeholder = info.placeholder || "";
				body = body.replace(
					`{a${position}}`,
					`<input id="a${position}" name="a${position}" type="text" autocomplete="off" placeholder="${placeholder}" class="custom-input fit-height">`,
				);
			} else if (info.type === "inputs-list") {
				const placeholder = escapeHtmlAttr(info.placeholder || "");
				const arr = Array.isArray(model[`a${position}`]) ? model[`a${position}`] : [];
				let str = `<div id="a${position}" class="users-input-list">`;
				const count = Math.max(arr.length + 1, 1);
				for (let i = 0; i < count; i++) {
					const value = arr[i] ? escapeHtmlAttr(arr[i]) : "";
					str += `<input id="inputs-list-${i}" value="${value}" type="text" autocomplete="off" placeholder="${placeholder}" class="custom-input template-input bottom-short-mrg fit-height">`;
				}
				str += `</div>`;
				replacePlaceholder(str);
			} else if (info.type === "sign-row") {
				body = body.replace(
					`{a${position}}`,
					`<input disabled type="text" autocomplete="off" class="custom-input max-assignment-width fit-height">`,
				);
			} else if (info.type === "sign-row-arr") {
				const placeholder = escapeHtmlAttr(info.placeholder || "");
				const arr = getCommissionNamesForSignBlock(position);
				let str = `<div id="a${position}" class="input-list">`;
				const count = Math.max(arr.length + 1, 1);
				for (let i = 0; i < count; i++) {
					const value = arr[i] ? escapeHtmlAttr(arr[i]) : "";
					str += `<div class="flexable"><input disabled type="text" autocomplete="off" class="custom-input max-assignment-width fit-height"><input disabled id="sign-list-${i}" value="${value}" type="text" autocomplete="off" placeholder="${placeholder}" class="custom-input template-input bottom-short-mrg fit-height"></div>`;
				}
				str += `</div>`;
				replacePlaceholder(str);
			} else if (info.type === "students-list") {
				// Keep a hidden marker; actual checkbox list is injected after selecting a group
				body = body.replace(`{a${position}}`, `<p class="hidden">{a${position}}</p>`);
			} else if (info.type === "text-area") {
				body = body.replace(
					`{a${position}}`,
					`<textarea id="a${position}" class="text-area" placeholder="Ввести текст"></textarea>`,
				);
			} else if (info.type === "drop-down-group" || info.type === "drop-down-group-not-draw") {
				// render select from groupData (best effort)
				const selectedValue = model[`a${position}`];
				const options = (groupData || [])
					.map((g, i) => {
						const name = g.groupName || g.name;
						const selected = selectedValue && name === selectedValue ? " selected " : "";
						return `<option ${selected} id="${position}-${i}">${name}</option>`;
					})
					.join("");
				body = body.replace(
					`{a${position}}`,
					`<select id="a${position}" class="select-list"><option selected disabled hidden></option>${options}</select>`,
				);
			} else if (info.type === "library") {
				const lib = libraryDatas[position] || [];
				const selectedValue = model[`a${position}`];
				const options = (lib || [])
					.map((item, i) => {
						const name = item.name || item.groupName || item.data;
						const selected = selectedValue && name === selectedValue ? " selected " : "";
						return `<option ${selected} id="${position}-${i}">${name}</option>`;
					})
					.join("");
				body = body.replace(
					`{a${position}}`,
					`<select id="a${position}" class="select-list"><option selected disabled hidden></option>${options}</select>`,
				);
			} else if (info.type === "list") {
				const list = listDatas[position] || [];
				const selectedValue = model[`a${position}`];
				const options = (list || [])
					.map((item, i) => {
						const name = item.name || item.groupName || item.data;
						const selected = selectedValue && name === selectedValue ? " selected " : "";
						return `<option ${selected} id="${position}-${i}">${name}</option>`;
					})
					.join("");
				body = body.replace(
					`{a${position}}`,
					`<select id="a${position}" class="select-list"><option selected disabled hidden></option>${options}</select>`,
				);
			} else if (info.type === "add-row-button") {
				body = body.replace(
					`{a${position}}`,
					`<button id="add-${position}" type="button" class="table-row-add">Add</button>`,
				);
			} else if (info.type === "remove-row-button") {
				body = body.replace(
					`{a${position}}`,
					`<button id="remove-${position}-1" type="button" class="table-row-add">Remove</button>`,
				);
			} else if (info.type === "table-input-string") {
				const placeholder = info.placeholder || "";
				const html = `<input id="col${position}-1" name="a${position}" type="text" autocomplete="off" placeholder="${placeholder}" class="custom-input template-table-input">`;
				replacePlaceholder(html);
			} else if (info.type === "table-input-number") {
				const html = `<input id="col${position}-1" name="a${position}" type="number" autocomplete="off" class="custom-input template-table-input">`;
				replacePlaceholder(html);
			} else if (info.type === "table-input-date") {
				const html = `<input id="col${position}-1" name="a${position}" type="date" autocomplete="off" class="custom-input date-input">`;
				replacePlaceholder(html);
			} else if (info.type === "table-number-and-date") {
				const html = `№<input id="0-col${position}-1" name="a${position}" type="number" autocomplete="off" class="custom-input template-table-input">от<input id="1-col${position}-1" name="a${position}" type="date" autocomplete="off" class="custom-input date-input">`;
				replacePlaceholder(html);
			} else if (info.type === "table-input-start-end-time") {
				const html = `<div class="flexable"><input id="startcol${position}-1" name="a${position}" type="time" autocomplete="off" class="custom-input compact-width"> - <input id="endcol${position}-1" name="a${position}" type="time" autocomplete="off" class="custom-input compact-width"></div>`;
				replacePlaceholder(html);
			} else if (info.type === "table-sign-part") {
				// Signature placeholder in table should be empty (Angular renders a div container)
				const html = `<div id="col${position}-1"></div>`;
				replacePlaceholder(html);
			} else if (info.type === "table-library") {
				const lib = tableLibDatas[position] || [];
				const options = (lib || [])
					.map((item, i) => `<option id="col${position}-1-${i}">${item.name || item.data}</option>`)
					.join("");
				const html = `<select id="col${position}-1" class="select-list"><option selected disabled hidden></option>${options}</select>`;
				replacePlaceholder(html);
			} else if (info.type === "table-list") {
				const list = tableListDatas[position] || [];
				const options = (list || [])
					.map((item, i) => `<option id="col${position}-1-${i}">${item.data || item.name}</option>`)
					.join("");
				const html = `<select id="col${position}-1" class="select-list"><option selected disabled hidden></option>${options}</select>`;
				replacePlaceholder(html);
			}

			// hidden fields
			if (info.hidden) {
				// Angular special-case: if previous info has entityId === 5, it uses id="entity-a{pos}"
				const prev = templateInfo.find(x => x.position === position - 1);
				if (prev?.entityId === 5) {
					body = body.replace(`id="a${position}"`, `id="entity-a${position}" class="hidden"`);
				} else {
					body = body.replace(`id="a${position}"`, `id="a${position}" class="hidden"`);
					body = body.replace(`<p>{a${position}}</p>`, `<p class="hidden">{a${position}}</p>`);
				}
			}
		});

		// table container and typography tweaks similar to Angular
		body = body.replace("<table", '<div class="template-table-container"><table class="template-table"');
		body = body.replace("</table>", "</table></div>");
		body = body.replace("<h1>", '<h1 class="center template-title">');
		body = body.replace("<h3>", '<h3 class="center template-title margin-h3">');
		body = body.replace("<h3>", '<h3 class="doc-form-sub-title">');
		body = body.replace("<h2>", '<h2 class="temp-sub-title center selected-font">');
		body = body.split("<p>").join('<p class="d-flex flex-wrap">');

		// apply stored values into html
		for (const k in model) {
			if (!model[k] || k === "tableData") continue;
			body = body.replace(`<input id="${k}"`, `<input id="${k}" value="${model[k]}"`);
			body = body.replace(
				`<textarea id="${k}" class="text-area" placeholder="Ввести текст"></textarea>`,
				`<textarea id="${k}" class="text-area" placeholder="Ввести текст">${model[k]}</textarea>`,
			);
		}

		setTableTds(tds);
		setTableRowCount(rowCount);

		return body;
	};

	const collectModelFromDom = () => {
		const root = containerRef.current;
		if (!root) return templateModel;

		const model = buildEmptyModel();

		// inputs
		root.querySelectorAll("input[id^='a']").forEach(inp => {
			const id = inp.getAttribute("id");
			if (id) model[id] = inp.value;
		});
		root.querySelectorAll("textarea[id^='a']").forEach(t => {
			const id = t.getAttribute("id");
			if (id) model[id] = t.value;
		});
		root.querySelectorAll("select[id^='a']").forEach(sel => {
			const id = sel.getAttribute("id");
			if (id) model[id] = sel.value;
		});

		// inputs-list values
		root.querySelectorAll(".users-input-list[id^='a']").forEach(wrapper => {
			const id = wrapper.getAttribute("id"); // a{position}
			if (!id) return;
			const values = [];
			wrapper.querySelectorAll("input[id^='inputs-list-']").forEach(inp => {
				if (inp.value && inp.value.trim()) values.push(inp.value.trim());
			});
			model[id] = values;
		});

		// sign-row-arr: read mirrored rows; if empty, copy from inputs-list (Angular often saves only a22)
		const signInfo = templateInfo.find(x => x.type === "sign-row-arr");
		const inputsListInfos = templateInfo.filter(x => x.type === "inputs-list");
		if (signInfo) {
			const signKey = `a${signInfo.position}`;
			const esc = CSS?.escape ? CSS.escape(signKey) : signKey;
			const block = root.querySelector(`#${esc}.input-list`);
			const signValues = [];
			if (block) {
				block.querySelectorAll("input[id^='sign-list-']").forEach(inp => {
					if (inp.value && inp.value.trim()) signValues.push(inp.value.trim());
				});
			}
			if (signValues.length) model[signKey] = signValues;
			else {
				const signArr = Array.isArray(model[signKey]) ? model[signKey] : [];
				if (!signArr.length) {
					for (const il of inputsListInfos) {
						const ik = `a${il.position}`;
						const list = Array.isArray(model[ik]) ? model[ik] : [];
						if (list.length) {
							model[signKey] = [...list];
							break;
						}
					}
				}
			}
		}

		// tableData: scan ids:
		// - col{pos}-{row}
		// - startcol{pos}-{row} / endcol{pos}-{row}
		// - 0-col{pos}-{row} / 1-col{pos}-{row} (table-number-and-date)
		const tableItems = [];
		const numberAndDateMap = new Map(); // key row-col -> { number, columnNumber, value: [] }

		root.querySelectorAll("input[id],select[id],textarea[id],div[id^='col']").forEach(el => {
			const id = el.getAttribute("id");
			if (!id) return;

			const mND = id.match(/^([01])-col(\d+)-(\d+)$/);
			if (mND) {
				const idx = Number(mND[1]);
				const col = mND[2];
				const row = mND[3];
				const key = `${row}-${col}`;
				const existing = numberAndDateMap.get(key) || { number: row, columnNumber: col, value: [], timeType: "" };
				existing.value[idx] = el.value;
				numberAndDateMap.set(key, existing);
				return;
			}

			const mStartEnd = id.match(/^(startcol|endcol)(\d+)-(\d+)$/);
			if (mStartEnd) {
				const timeType = mStartEnd[1] === "startcol" ? "start" : "end";
				const col = mStartEnd[2];
				const row = mStartEnd[3];
				tableItems.push({ number: row, columnNumber: col, value: el.value, timeType });
				return;
			}

			const mCol = id.match(/^col(\d+)-(\d+)$/);
			if (mCol) {
				const col = mCol[1];
				const row = mCol[2];
				const value = el.tagName === "DIV" ? "" : el.value;
				if (value !== "" && value !== null && value !== undefined) {
					tableItems.push({ number: row, columnNumber: col, value, timeType: "" });
				}
			}
		});

		numberAndDateMap.forEach(v => {
			if ((v.value || []).some(x => x)) tableItems.push(v);
		});

		model.tableData = tableItems;

		// students-list values (checked checkboxes)
		const studentsPos = lastStudentsListPosRef.current;
		if (studentsPos) {
			const key = `a${studentsPos}`;
			const values = [];
			root.querySelectorAll(".students-container input[type='checkbox']").forEach(cb => {
				if (cb.checked) {
					const lbl = root.querySelector(`label[for="${cb.id}"]`);
					if (lbl?.textContent) values.push(lbl.textContent.trim());
				}
			});
			model[key] = values;
		}
		return model;
	};

	const handleFieldChange = useCallback(async (e) => {
		const target = e.target;
		if (!target) return;

		// group dropdown: fetch students-list and/or fill students in table
		if (target.tagName === "SELECT" && /^a\d+$/.test(target.id || "")) {
			const pos = Number(String(target.id).substring(1));
			const selectedName = target.value;

			// mark educational unit dropdown changes as user-driven (important for edit/update mode)
			if (eduPosRef.current != null && pos === eduPosRef.current) {
				eduChangedByUserRef.current = true;
			}

			// Persist selection immediately so dependent logic runs without blur/outside click
			setTemplateModel(prev => ({ ...prev, [`a${pos}`]: selectedName }));

			const division = (groupData || []).find(g => (g.groupName || g.name) === selectedName);

			// entity "other" toggle (Angular: show entity-a{pos+1})
			const nextPos = pos + 1;
			const entityEl = containerRef.current?.querySelector(`#${CSS?.escape ? CSS.escape(`entity-a${nextPos}`) : `entity-a${nextPos}`}`);
			if (entityEl) {
				if (selectedName === "Другое (вписать вручную)") {
					entityEl.classList.remove("hidden");
				} else {
					entityEl.classList.add("hidden");
					entityEl.value = "";
				}
			}

			if (division?.id) {
				const nextInfo = templateInfo.find(x => x.position === nextPos);
				if (nextInfo?.type === "students-list") {
					await setStudentsListForDivision(division.id, nextPos);
				}
				if (templateInfo.some(x => x.type === "students-in-table")) {
					await fillStudentsInTable(division.id);
				}
			}
		}

		// checkbox change: update students list in model
		if (target.type === "checkbox" && target.closest?.(".students-container")) {
			const payload = collectModelFromDom();
			setTemplateModel(payload);
		}
	}, [collectModelFromDom, groupData, templateInfo]);

	const syncSignRowArrFromInputsList = useCallback((values) => {
		const root = containerRef.current;
		if (!root) return;

		const signInfo = templateInfo.find(x => x.type === "sign-row-arr");
		if (!signInfo) return;

		const placeholder = escapeHtmlAttr(signInfo.placeholder || "");
		const signKey = `a${signInfo.position}`;
		const esc = CSS?.escape ? CSS.escape(signKey) : signKey;
		const block = root.querySelector(`#${esc}.input-list`);
		if (!block) return;

		let html = "";
		const count = Math.max(values.length + 1, 1);
		for (let i = 0; i < count; i++) {
			const val = values[i] ? escapeHtmlAttr(values[i]) : "";
			html += `<div class="flexable"><input disabled type="text" autocomplete="off" class="custom-input max-assignment-width fit-height"><input disabled id="sign-list-${i}" value="${val}" type="text" autocomplete="off" placeholder="${placeholder}" class="custom-input template-input bottom-short-mrg fit-height"></div>`;
		}
		block.innerHTML = html;
	}, [templateInfo]);

	const handleFocusOut = useCallback((e) => {
		const target = e.target;
		if (!target?.id) return;

		// inputs-list behavior like Angular: when last input filled, add a new empty input
		if (/^inputs-list-\d+$/.test(target.id)) {
			const wrapper = target.closest?.(".users-input-list");
			if (!wrapper) return;

			const inputs = Array.from(wrapper.querySelectorAll("input[id^='inputs-list-']"));
			const last = inputs[inputs.length - 1];
			const lastIndex = inputs.length - 1;

			const values = inputs
				.map(i => (i.value || "").trim())
				.filter(Boolean);

			// If user typed into the last input, append a new empty one
			if (last && last.id === target.id && (target.value || "").trim()) {
				const nextId = `inputs-list-${lastIndex + 1}`;
				if (!wrapper.querySelector(`#${CSS?.escape ? CSS.escape(nextId) : nextId}`)) {
					const placeholder = target.getAttribute("placeholder") || "";
					const el = document.createElement("input");
					el.setAttribute("id", nextId);
					el.setAttribute("type", "text");
					el.setAttribute("autocomplete", "off");
					el.setAttribute("placeholder", placeholder);
					el.setAttribute("class", "custom-input template-input bottom-short-mrg fit-height");
					el.value = "";
					wrapper.appendChild(el);
				}
			}

			// Mirror to sign-row-arr blocks
			syncSignRowArrFromInputsList(values);

			// Keep model in sync for inputs-list and sign-row-arr (if present)
			const modelKey = wrapper.getAttribute("id"); // a{position}
			const signRowArrPos = templateInfo.find(x => x.type === "sign-row-arr")?.position;
			setTemplateModel(prev => ({
				...prev,
				...(modelKey ? { [modelKey]: values } : {}),
				...(signRowArrPos ? { [`a${signRowArrPos}`]: values } : {}),
			}));
		}
	}, [syncSignRowArrFromInputsList, templateInfo]);

	useEffect(() => {
		// Native event bridge for content rendered via dangerouslySetInnerHTML.
		// React synthetic onChange may not consistently fire for dynamically injected DOM.
		const root = containerRef.current;
		if (!root) return;

		const handler = (ev) => {
			handleFieldChange(ev);
		};

		root.addEventListener("change", handler, true);
		root.addEventListener("input", handler, true);
		root.addEventListener("focusout", handleFocusOut, true);

		return () => {
			root.removeEventListener("change", handler, true);
			root.removeEventListener("input", handler, true);
			root.removeEventListener("focusout", handleFocusOut, true);
		};
	}, [handleFieldChange, handleFocusOut]);

	const onClick = e => {
		const target = e.target;
		if (!target?.id || target.type !== "button") return;
		if (target.id.startsWith("add-")) {
			const root = containerRef.current;
			const table = root?.querySelector("table");
			const tbody = table?.querySelector("tbody") || table;
			if (!tbody) return;
			const trs = tbody.querySelectorAll("tr");
			if (!trs?.length) return;
			const last = trs[trs.length - 1];
			const newRow = trs.length + 1;
			const clone = last.cloneNode(true);
			clone.querySelectorAll("input,select,textarea").forEach(el => {
				const id = el.getAttribute("id");
				if (id) {
					const nextId = id.replace(/-\d+$/, `-${newRow}`);
					el.setAttribute("id", nextId);
				}
				if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") el.value = "";
				if (el.tagName === "SELECT") el.selectedIndex = 0;
			});
			const firstTd = clone.querySelector("td");
			if (firstTd) firstTd.textContent = String(newRow);
			tbody.appendChild(clone);
		} else if (target.id.startsWith("remove-")) {
			const root = containerRef.current;
			const table = root?.querySelector("table");
			const tbody = table?.querySelector("tbody") || table;
			if (!tbody) return;
			const trs = tbody.querySelectorAll("tr");
			if (trs.length <= 1) {
				// clear first row values
				trs[0]?.querySelectorAll("input,select,textarea").forEach(el => {
					if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") el.value = "";
					if (el.tagName === "SELECT") el.selectedIndex = 0;
				});
				return;
			}
			tbody.removeChild(trs[trs.length - 1]);
		}
	};

	useImperativeHandle(ref, () => ({
		getPayload: () => {
			const payload = collectModelFromDom();
			setTemplateModel(payload);
			setTableData(Array.isArray(payload.tableData) ? payload.tableData : []);
			onSubmitTemplate && onSubmitTemplate(payload);
			return payload;
		},
	}));

	return renderedHtml ? (
		<div className="document-form">
			<div className="document-styles">
				<div className="template-form-root template-container">
					<div
						ref={containerRef}
						onClick={onClick}
						onChange={handleFieldChange}
						onBlur={() => {
							// keep state in sync so submit payload includes latest values
							const payload = collectModelFromDom();
							setTemplateModel(payload);
							setTableData(Array.isArray(payload.tableData) ? payload.tableData : []);
						}}
						dangerouslySetInnerHTML={{ __html: renderedHtml }}
					/>

				</div>
			</div>
		</div>
	) : null;
});

export default TemplateDocumentForm;

